import { cn } from "@/lib/utils";
import { Zap, Package, Database, Link, Bot, BarChart2, Sparkles, ArrowRight } from "lucide-react";

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const features: Feature[] = [
  {
    icon: <Zap className="w-6 h-6 text-sky-600" />,
    title: "Edge-first architecture",
    description:
      "Deployed on 300+ edge locations globally. Sub-50ms responses from anywhere in the world.",
  },
  {
    icon: <Package className="w-6 h-6 text-sky-600" />,
    title: "Type-safe SDKs",
    description:
      "First-class TypeScript and Python SDKs with full autocomplete and type inference out of the box.",
  },
  {
    icon: <Database className="w-6 h-6 text-sky-600" />,
    title: "Smart caching",
    description:
      "Intelligent response caching means you only pay for unique requests. 80%+ cache hit rates typical.",
  },
  {
    icon: <Link className="w-6 h-6 text-sky-600" />,
    title: "Unified endpoint",
    description:
      "One API for current, forecast, historical, and alerts. No more juggling multiple endpoints.",
  },
  {
    icon: <Bot className="w-6 h-6 text-sky-600" />,
    title: "x402 for AI agents",
    description:
      "Native support for HTTP 402 payments. AI agents can autonomously pay per request with USDC.",
  },
  {
    icon: <BarChart2 className="w-6 h-6 text-sky-600" />,
    title: "Real-time dashboard",
    description:
      "Monitor usage, costs, and performance in real-time. Set alerts before you hit limits.",
  },
];

interface FeaturesProps {
  className?: string;
}

export function Features({ className }: FeaturesProps) {
  return (
    <section
      id="features"
      className={cn("py-20 px-4 sm:px-6 lg:px-8 bg-white", className)}
    >
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-sky-100 text-sky-700 text-sm font-medium rounded-full mb-4">
            <Sparkles className="w-3.5 h-3.5" /> Features
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
            Built for modern development
          </h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">
            Everything you need to ship weather features fast, without the
            legacy API headaches.
          </p>
        </div>

        {/* Features grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="group p-6 bg-white rounded-2xl border border-slate-200 hover:border-sky-200 hover:shadow-lg hover:shadow-sky-100/50 transition-all duration-300"
            >
              {/* Icon */}
              <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-sky-50 to-sky-100 rounded-xl mb-4 group-hover:scale-110 transition-transform">
                {feature.icon}
              </div>

              {/* Content */}
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                {feature.title}
              </h3>
              <p className="text-slate-500 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-slate-500 mb-4">
            Ready to see it in action?
          </p>
          <a
            href="/docs"
            className="inline-flex items-center gap-2 px-6 py-3 text-sky-600 font-semibold hover:text-sky-700 transition-colors"
          >
            Explore the documentation
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

export default Features;
